<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Theme Categories</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Organize themes into categories</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="isLoading"
                                                @click="loadData"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': isLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                @click="showCreateModal = true"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create Category
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-[200px]">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search categories..."
                                                                class="w-full max-w-[300px] py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">{{ isFilteringByOffering ? 'Searching…' : `${filteredItems.length} categories` }}</span>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <label class="text-sm text-gray-500">Offering:</label>
                                                <div class="relative min-w-[260px]" ref="filterOfferingDropdownRef">
                                                        <div class="relative">
                                                                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                                </svg>
                                                                <input
                                                                        v-model="filterOfferingSearch"
                                                                        type="text"
                                                                        :placeholder="selectedFilterOffering ? '' : 'All Offerings — search by ID, name, external ID...'"
                                                                        class="w-full py-2 pl-9 pr-8 border border-gray-300 rounded-md text-sm bg-white box-border outline-none"
                                                                        @focus="filterOfferingDropdownOpen = true"
                                                                        @input="onFilterOfferingInput"
                                                                >
                                                                <button
                                                                        v-if="selectedFilterOffering || filterOfferingSearch"
                                                                        type="button"
                                                                        class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer p-1"
                                                                        title="Clear"
                                                                        @click="clearFilterOffering"
                                                                >
                                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                                        </svg>
                                                                </button>
                                                        </div>
                                                        <div
                                                                v-if="filterOfferingDropdownOpen && filteredFilterOfferings.length > 0"
                                                                class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-[260px] overflow-y-auto"
                                                        >
                                                                <div
                                                                        class="py-2 px-3 text-sm cursor-pointer hover:bg-orange-1 transition-colors text-gray-600 border-b border-gray-100"
                                                                        @mousedown.prevent="clearFilterOffering"
                                                                >
                                                                        All Offerings
                                                                </div>
                                                                <div
                                                                        v-for="off in filteredFilterOfferings"
                                                                        :key="off.id"
                                                                        class="py-2 px-3 text-sm cursor-pointer hover:bg-orange-1 transition-colors"
                                                                        :class="{ 'bg-orange-1': selectedFilterOffering && selectedFilterOffering.id === off.id }"
                                                                        @mousedown.prevent="selectFilterOffering(off)"
                                                                >
                                                                        <span class="font-mono text-gray-500">{{ off.id }}</span>
                                                                        <span class="mx-1 text-gray-300">—</span>
                                                                        <span class="font-medium text-gray-900">{{ off.name }}</span>
                                                                        <span v-if="off.externalid" class="ml-2 text-xs text-gray-400">({{ off.externalid }})</span>
                                                                </div>
                                                        </div>
                                                        <div
                                                                v-else-if="filterOfferingDropdownOpen && filterOfferingSearch.trim() && filteredFilterOfferings.length === 0"
                                                                class="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg"
                                                        >
                                                                <div class="py-3 px-3 text-sm text-gray-400 text-center">No offerings found</div>
                                                        </div>
                                                </div>
                                                <svg
                                                        v-if="isFilteringByOffering"
                                                        class="w-4 h-4 text-orange-7 animate-spin"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                >
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <label class="text-sm text-gray-500">Show:</label>
                                                <select v-model="pageSize" class="py-2 px-3 border border-gray-300 rounded-md text-sm bg-white">
                                                        <option :value="10">10</option>
                                                        <option :value="25">25</option>
                                                        <option :value="50">50</option>
                                                        <option :value="100">100</option>
                                                </select>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading categories...</span>
                        </div>

                        <!-- Table Card -->
                        <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="overflow-x-auto">
                                        <table class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="text-left py-3 px-4 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase w-[80px]">Icon</th>
                                                                <th class="text-left py-3 px-4 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">ID</th>
                                                                <th class="text-left py-3 px-4 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">Name</th>
                                                                <th class="text-left py-3 px-4 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">Locale</th>
                                                                <th class="text-left py-3 px-4 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">Serial #</th>
                                                                <th class="text-right py-3 px-4 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase w-[120px]">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr v-if="isFilteringByOffering">
                                                                <td colspan="6" class="text-center py-12 px-4">
                                                                        <div class="flex justify-center items-center">
                                                                                <svg class="w-6 h-6 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                </svg>
                                                                                <span class="ml-3 text-gray-500 text-sm">Searching categories...</span>
                                                                        </div>
                                                                </td>
                                                        </tr>
                                                        <tr v-else-if="paginatedItems.length === 0">
                                                                <td colspan="6" class="text-center py-12 px-4">
                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                                        </svg>
                                                                        <p class="text-gray-500 text-sm m-0">No categories found</p>
                                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Try adjusting your search or create a new category</p>
                                                                </td>
                                                        </tr>
                                                        <tr
                                                                v-for="item in paginatedItems"
                                                                :key="item.id"
                                                                class="border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                                                                @click="editItem(item.id)"
                                                        >
                                                                <td class="py-3 px-4 text-sm text-gray-900 align-middle">
                                                                        <img
                                                                                v-if="item.icon"
                                                                                :src="item.icon"
                                                                                :alt="item.name || 'Category'"
                                                                                class="w-10 h-10 rounded object-cover border border-gray-200"
                                                                        >
                                                                        <div v-else class="w-10 h-10 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">-</div>
                                                                </td>
                                                                <td class="py-3 px-4 text-sm font-mono text-gray-500 align-middle">{{ item.id }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-900 font-medium align-middle">{{ item.name || '-' }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-500 align-middle">{{ item.locale || '-' }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-500 align-middle">{{ item.serialnumber ?? '-' }}</td>
                                                                <td class="py-3 px-4 text-sm text-right align-middle">
                                                                        <div class="flex justify-end gap-2">
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                        title="Edit"
                                                                                        @click.stop="editItem(item.id)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                        </svg>
                                                                                </button>
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600"
                                                                                        title="Delete"
                                                                                        @click.stop="confirmDelete(item)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                                                                                        </svg>
                                                                                </button>
                                                                        </div>
                                                                </td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="flex justify-between items-center py-4 px-6 border-t border-gray-200 flex-wrap gap-4">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ startRow }} to {{ endRow }} of {{ totalRows }} entries
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="goToPage(currentPage - 1)"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-[2.5rem]"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300'"
                                                        @click="goToPage(page)"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="goToPage(currentPage + 1)"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="closeCreateModal"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-[400px] m-4">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Create New Category</h3>
                                        <button
                                                type="button"
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1"
                                                @click="closeCreateModal"
                                        >
                                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                                <input
                                                        v-model="newItemName"
                                                        type="text"
                                                        placeholder="Enter category name"
                                                        class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border outline-none"
                                                        @keydown.enter="createItem"
                                                >
                                        </div>
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200">
                                        <button
                                                type="button"
                                                class="py-2 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                @click="closeCreateModal"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                class="py-2 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                :disabled="!newItemName.trim() || isCreating"
                                                @click="createItem"
                                        >
                                                {{ isCreating ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ThemeCategoryModel, OfferingModel, ThemeModel } from '@sosocio/creator/interfaces';
import Swal from 'sweetalert2';
import ThemeCategoryService from 'services/ThemeCategoryService';
import ThemeService from 'services/ThemeService';
import { httpClient } from 'utils/http';

export default defineComponent({
        name: 'ThemeCategoriesPage',
        setup() {
                const router = useRouter();

                const items = ref<ThemeCategoryModel[]>([]);
                const searchQuery = ref('');
                const currentPage = ref(1);
                const pageSize = ref(25);
                const isLoading = ref(true);
                const showCreateModal = ref(false);
                const newItemName = ref('');
                const isCreating = ref(false);

                const offerings = ref<OfferingModel[]>([]);
                const selectedFilterOffering = ref<OfferingModel | null>(null);
                const filterOfferingSearch = ref('');
                const filterOfferingDropdownOpen = ref(false);
                const filterOfferingDropdownRef = ref<HTMLElement | null>(null);
                const allowedCategoryIds = ref<Set<number> | null>(null);
                const isFilteringByOffering = ref(false);

                const filteredItems = computed(() => {
                        let result = items.value;
                        if (allowedCategoryIds.value) {
                                const allowed = allowedCategoryIds.value;
                                result = result.filter(item => allowed.has(item.id));
                        }
                        if (!searchQuery.value.trim()) {
                                return result;
                        }
                        const query = searchQuery.value.toLowerCase();
                        return result.filter(item =>
                                String(item.name ?? '').toLowerCase().includes(query) ||
                                String(item.locale ?? '').toLowerCase().includes(query) ||
                                String(item.id ?? '').includes(query)
                        );
                });

                const filteredFilterOfferings = computed(() => {
                        const query = filterOfferingSearch.value.toLowerCase().trim();
                        const source = offerings.value;
                        if (!query) return source.slice(0, 50);
                        return source.filter(off =>
                                String(off.id).includes(query) ||
                                String(off.name ?? '').toLowerCase().includes(query) ||
                                (off.externalid && String(off.externalid).toLowerCase().includes(query)) ||
                                (off.description && String(off.description).toLowerCase().includes(query))
                        ).slice(0, 50);
                });

                const totalRows = computed(() => filteredItems.value.length);
                const totalPages = computed(() => Math.max(1, Math.ceil(totalRows.value / pageSize.value)));
                const startRow = computed(() => totalRows.value === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1);
                const endRow = computed(() => Math.min(currentPage.value * pageSize.value, totalRows.value));

                const paginatedItems = computed(() => {
                        const start = (currentPage.value - 1) * pageSize.value;
                        return filteredItems.value.slice(start, start + pageSize.value);
                });

                const visiblePages = computed(() => {
                        const pages: number[] = [];
                        const total = totalPages.value;
                        const current = currentPage.value;
                        const maxButtons = 7;
                        if (total <= maxButtons) {
                                for (let i = 1; i <= total; i++) pages.push(i);
                                return pages;
                        }
                        let start = Math.max(1, current - 3);
                        let end = Math.min(total, start + maxButtons - 1);
                        if (end - start < maxButtons - 1) {
                                start = Math.max(1, end - maxButtons + 1);
                        }
                        for (let i = start; i <= end; i++) pages.push(i);
                        return pages;
                });

                watch([searchQuery, pageSize, selectedFilterOffering], () => {
                        currentPage.value = 1;
                });

                const loadOfferings = async () => {
                        try {
                                const response = await httpClient.get<OfferingModel[]>('/api/offering?limit=0');
                                offerings.value = response.data;
                        } catch (error: any) {
                                console.error('Failed to load offerings:', error);
                        }
                };

                let offeringFilterToken = 0;
                const applyOfferingFilter = async (offering: OfferingModel | null) => {
                        const token = ++offeringFilterToken;
                        if (!offering) {
                                allowedCategoryIds.value = null;
                                isFilteringByOffering.value = false;
                                return;
                        }
                        // Reset immediately so stale results from a previous offering aren't shown
                        allowedCategoryIds.value = new Set();
                        isFilteringByOffering.value = true;
                        try {
                                const params = new URLSearchParams({
                                        limit: '0',
                                        where: JSON.stringify({
                                                groupid: offering.groupid,
                                                typeid: offering.typeid,
                                        }),
                                });
                                const themes = await ThemeService.getAll(params) as ThemeModel[];
                                if (token !== offeringFilterToken) return;

                                const categoryIdSet = new Set<number>();
                                let partialFailures = 0;
                                if (themes.length > 0) {
                                        const results = await Promise.all(
                                                themes.map(t =>
                                                        ThemeService.getAllThemeCategories(t.id).catch(() => {
                                                                partialFailures++;
                                                                return [] as ThemeCategoryModel[];
                                                        }),
                                                ),
                                        );
                                        if (token !== offeringFilterToken) return;
                                        for (const list of results) {
                                                for (const cat of list) {
                                                        if (cat && typeof cat.id === 'number') categoryIdSet.add(cat.id);
                                                }
                                        }
                                }
                                allowedCategoryIds.value = categoryIdSet;
                                if (partialFailures > 0) {
                                        Swal.fire({
                                                toast: true,
                                                position: 'top-end',
                                                icon: 'warning',
                                                title: `Some themes (${partialFailures}) couldn't be checked — results may be incomplete`,
                                                showConfirmButton: false,
                                                timer: 4000,
                                        });
                                }
                        } catch (error: any) {
                                if (token !== offeringFilterToken) return;
                                console.error('Failed to filter categories by offering:', error);
                                allowedCategoryIds.value = new Set();
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to filter by offering',
                                        icon: 'error',
                                });
                        } finally {
                                if (token === offeringFilterToken) {
                                        isFilteringByOffering.value = false;
                                }
                        }
                };

                const onFilterOfferingInput = () => {
                        filterOfferingDropdownOpen.value = true;
                };

                const selectFilterOffering = (off: OfferingModel) => {
                        selectedFilterOffering.value = off;
                        filterOfferingSearch.value = off.name || String(off.id);
                        filterOfferingDropdownOpen.value = false;
                        applyOfferingFilter(off);
                };

                const clearFilterOffering = () => {
                        selectedFilterOffering.value = null;
                        filterOfferingSearch.value = '';
                        filterOfferingDropdownOpen.value = false;
                        applyOfferingFilter(null);
                };

                const handleFilterOfferingClickOutside = (event: MouseEvent) => {
                        const el = filterOfferingDropdownRef.value;
                        if (!el) return;
                        if (!el.contains(event.target as Node)) {
                                filterOfferingDropdownOpen.value = false;
                                if (selectedFilterOffering.value) {
                                        filterOfferingSearch.value = selectedFilterOffering.value.name || String(selectedFilterOffering.value.id);
                                }
                        }
                };

                const goToPage = (page: number) => {
                        if (page >= 1 && page <= totalPages.value) {
                                currentPage.value = page;
                        }
                };

                const loadData = async () => {
                        isLoading.value = true;
                        try {
                                const params = new URLSearchParams({
                                        orderby: 'serialnumber ASC',
                                        limit: '0',
                                });
                                const data = await ThemeCategoryService.getAll(params);
                                items.value = data;
                        } catch (error: any) {
                                console.error('Failed to load categories:', error);
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to load categories',
                                        icon: 'error',
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const editItem = (id: number) => {
                        router.push(`/themecategory/${id}`);
                };

                const confirmDelete = async (item: ThemeCategoryModel) => {
                        const result = await Swal.fire({
                                title: 'Delete Category?',
                                text: `Are you sure you want to delete "${item.name || 'this item'}"? This action cannot be undone.`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                cancelButtonColor: '#6b7280',
                                confirmButtonText: 'Yes, delete it',
                                cancelButtonText: 'Cancel',
                        });

                        if (result.isConfirmed) {
                                try {
                                        await ThemeCategoryService.delete(item.id);
                                        items.value = items.value.filter(i => i.id !== item.id);
                                        Swal.fire({
                                                toast: true,
                                                position: 'top-end',
                                                icon: 'success',
                                                title: 'Category deleted successfully',
                                                showConfirmButton: false,
                                                timer: 3000,
                                        });
                                } catch (error: any) {
                                        Swal.fire({
                                                title: 'Error',
                                                text: error.message || 'Failed to delete category',
                                                icon: 'error',
                                        });
                                }
                        }
                };

                const closeCreateModal = () => {
                        if (isCreating.value) return;
                        showCreateModal.value = false;
                        newItemName.value = '';
                };

                const createItem = async () => {
                        if (isCreating.value) return;
                        if (!newItemName.value.trim()) return;

                        isCreating.value = true;
                        try {
                                const newCategory = await ThemeCategoryService.create({
                                        name: newItemName.value.trim(),
                                });
                                showCreateModal.value = false;
                                newItemName.value = '';
                                router.push(`/themecategory/${newCategory.id}`);
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to create category',
                                        icon: 'error',
                                });
                        } finally {
                                isCreating.value = false;
                        }
                };

                onMounted(() => {
                        loadData();
                        loadOfferings();
                        document.addEventListener('mousedown', handleFilterOfferingClickOutside);
                });

                onBeforeUnmount(() => {
                        document.removeEventListener('mousedown', handleFilterOfferingClickOutside);
                });

                return {
                        items, searchQuery, currentPage, pageSize, isLoading, showCreateModal, newItemName, isCreating,
                        filteredItems, totalRows, totalPages, startRow, endRow, paginatedItems, visiblePages,
                        selectedFilterOffering, filterOfferingSearch, filterOfferingDropdownOpen,
                        filterOfferingDropdownRef, filteredFilterOfferings, isFilteringByOffering,
                        goToPage, loadData, editItem, confirmDelete, createItem, closeCreateModal,
                        onFilterOfferingInput, selectFilterOffering, clearFilterOffering,
                };
        },
});
</script>
